import find from 'lodash/find';
import filter from 'lodash/filter';
import remove from 'lodash/remove';
import unionBy from 'lodash/unionBy';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import { Mapper } from 'modules/core';
import { resolveErrorMessage } from 'modules/errors';
import {
  postRole,
  patchRole,
  deleteRole,
  fetchRole,
  fetchProjectRoles,
  fetchSharedProjectRole,
} from './api';

export const loadProjectRole = createAsyncThunk(
  'projectRoles/loadProjectRole',
  async ({ producerId, projectId, roleId }) =>
    await fetchRole(producerId, projectId, roleId),
);

export const loadProjectRoles = createAsyncThunk(
  'projectRoles/loadProjectRoles',
  async ({ producerId, projectId }) =>
    await fetchProjectRoles(producerId, projectId),
);

export const loadSharedProjectRole = createAsyncThunk(
  'projectRole/loadShared', // TODO: should this be 'projectRoleS'?
  async token => await fetchSharedProjectRole(token),
);

export const createRole = createAsyncThunk(
  'projectRoles/create',
  async ({ producerId, projectId, values }) =>
    await postRole(producerId, projectId, values),
);

export const updateRole = createAsyncThunk(
  'projectRoles/update',
  async ({ producerId, projectId, roleId, values }) =>
    await patchRole(producerId, projectId, roleId, values),
);

export const removeRole = createAsyncThunk(
  'projectRoles/delete',
  async ({ producerId, projectId, roleId }) =>
    await deleteRole(producerId, projectId, roleId),
);

export const setSubmitError = createAsyncThunk(
  //TODO: ???? I think we might be able to set this to a normal action
  'projectRoles/setError',
  async error => error,
);

const projectRolesSlice = createSlice({
  name: 'projectRoles',
  initialState: {
    topCandidatesSort: 'fullName',
    auditionsSort: '-createdAt',
    loading: false,
    submitError: null,
    topCandidatesError: false,
    roles: [], // TODO: turn arrays of data to object maps (AHAB-597)
    sharedRole: null,
  },
  reducers: {
    setTopCandidatesSort(state, action) {
      state.topCandidatesSort = action.payload;
    },
    setAuditionsSort(state, action) {
      state.auditionsSort = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadProjectRole.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadProjectRole.fulfilled, (state, action) => {
        const loadedProjectRole = action.payload.data;
        state.loading = false;
        state.roles = Mapper.addOrReplace(state.roles, loadedProjectRole);
      })
      .addCase(loadSharedProjectRole.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadSharedProjectRole.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedRole = action.payload.data;
      })
      .addCase(loadSharedProjectRole.rejected, (state, action) => {
        state.submitError = resolveErrorMessage(action.error);
      })
      .addCase(loadProjectRoles.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadProjectRoles.fulfilled, (state, action) => {
        const loadedProjectRoles = action.payload.data;
        state.loading = false;
        state.roles = unionBy(loadedProjectRoles, state.roles, 'id');
      })
      .addCase(setSubmitError.fulfilled, (state, action) => {
        state.submitError = action.payload;
      })
      .addCase(removeRole.fulfilled, (state, action) => {
        const deletedRoleId = action.meta.arg.roleId;
        remove(state.roles, role => role.id === deletedRoleId);
      })
      .addCase(createRole.fulfilled, (state, action) => {
        const newRole = action.payload.data;
        state.roles = [...state.roles, newRole];
        state.submitError = false;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.submitError = resolveErrorMessage(action.error);
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        const newRole = action.payload.data;
        state.roles = Mapper.addOrReplace(state.roles, newRole);
        state.submitError = false;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.submitError = resolveErrorMessage(action.error);
      });
  },
  selectors: {
    getRoles: state => state.roles,
    getSharedRole: state => state.sharedRole,
    getLoading: state => state.loading,
    getSubmitError: state => state.submitError,
    getAuditionsSort: state => state.auditionsSort,
    getTopCandidatesSort: state => state.topCandidatesSort,
  },
});

export const {
  setTopCandidatesSort,
  setAuditionsSort,
} = projectRolesSlice.actions;
export const reducer = projectRolesSlice.reducer;
export const {
  getRoles,
  getSharedRole,
  getLoading,
  getSubmitError,
  getAuditionsSort,
  getTopCandidatesSort,
} = projectRolesSlice.selectors;

export const getRolesByProjectId = projectId =>
  createSelector(
    getRoles,
    stateRoles => filter(stateRoles, role => role.projectId === projectId),
  );

export const getRoleById = roleId =>
  createSelector(
    getRoles,
    stateRoles => find(stateRoles, { id: roleId }),
  );
